import { useEffect } from 'react'
import { Icon } from '../Icon'
import type { LiveShift } from '../../types'

// PersonDutyModal — opens when a manager taps a row in the Who's On
// list (Live View PR L4 / spec 33, v5 prototype L3789-3868). Shows
// the person's shift status, sign-on / sign-off times, and their
// avatar + name.
//
// Visible variation from the prototype: the v5 modal also rendered a
// "Today's Duties" list of role-specific tasks (hard-coded per role
// in the mock). We don't have a real data source for per-role task
// lists yet — surfacing a placeholder rather than fake data. Adding
// a duties-by-user-id endpoint is a follow-up scope (new query on
// dbo.RosterAssignments for the target user_id + date).

interface Props {
  shift: LiveShift
  onClose: () => void
}

export function PersonDutyModal({ shift, onClose }: Props) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [])

  const name = shift.userName ?? 'Unknown'
  const initials = shift.userAvatar ?? '?'
  const isOn = shift.signedOffAt == null
  const statusLabel = isOn
    ? '● On Shift Now'
    : shift.signedOffAt != null
      ? 'Signed Off'
      : 'Not Yet On'
  const statusColour = isOn ? '#0a4a3a' : '#5a5a5a'

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Duty detail for ${name}`}
      className="fixed inset-0 z-[500] flex items-center justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.7)', padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-parchment text-ink rounded-xl w-full"
        style={{
          maxWidth: 600,
          maxHeight: '90vh',
          overflow: 'auto',
          padding: 28,
          boxShadow: '0 24px 60px -10px rgba(0,0,0,0.45)'
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              aria-hidden
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'rgba(155, 127, 42, 0.4)',
                color: '#fdfbf6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 17,
                fontWeight: 700
              }}
            >
              {initials}
            </div>
            <div>
              <div className="font-display" style={{ fontSize: 22, fontWeight: 600 }}>
                {name}
              </div>
              <div className="text-sm text-ink-soft">
                Day {shift.signedOnDay} sign-on
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-ink-soft hover:text-ink rounded p-1.5"
          >
            <Icon name="x" size={16} />
          </button>
        </div>

        <div
          className="rounded-md p-4 mb-4"
          style={{
            background: isOn ? '#dcebe5' : '#f5edd5',
            border: `1px solid ${isOn ? '#0a4a3a' : '#d8d2c4'}`
          }}
        >
          <div
            className="text-xs uppercase font-bold"
            style={{ color: statusColour, letterSpacing: '0.06em' }}
          >
            {statusLabel}
          </div>
          <div
            className="grid mt-2"
            style={{ gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center' }}
          >
            <div>
              {shift.signedOnAt && (
                <div className="text-sm">
                  <span className="text-ink-soft">Signed on at </span>
                  <span className="font-mono font-semibold">{formatClockTime(shift.signedOnAt)}</span>
                </div>
              )}
              {shift.signedOffAt && (
                <div className="text-sm mt-1">
                  <span className="text-ink-soft">Signed off at </span>
                  <span className="font-mono font-semibold">{formatClockTime(shift.signedOffAt)}</span>
                </div>
              )}
            </div>
            <div className="text-xs text-ink-soft" style={{ textAlign: 'right' }}>
              {isOn
                ? 'Currently on site'
                : shift.signedOffAt
                  ? 'Shift complete'
                  : 'Awaiting check-in'}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h4
            className="text-xs uppercase font-bold text-ink-soft mb-2"
            style={{ letterSpacing: '0.06em' }}
          >
            Duties for today
          </h4>
          <div className="bg-white border border-line rounded-md p-3 text-sm text-ink-soft">
            Per-person duty breakdown lands in a follow-up. For now you can see
            this person's roster assignments on the event Roster tab.
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded text-sm font-semibold text-ink-soft hover:bg-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

function formatClockTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '--:--'
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
}
